import React from 'react';
import PropTypes from 'prop-types';
import useForm from './useForm';
import classNames from 'classnames';

// Define styles as a JavaScript object
const styles = {
  customForm: {
    maxWidth: '500px',
    margin: '2rem auto',
    padding: '2rem',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  fieldGroup: {
    marginBottom: '1.5rem',
  },
  formField: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem',
  },
  nestedField: {
    marginLeft: '2rem',
    marginBottom: '0.5rem',
  },
  label: {
    fontSize: '1rem',
    fontWeight: 500,
    color: '#333',
    flex: '0 0 120px',
  },
  input: {
    flex: 1,
    padding: '0.75rem',
    border: '1px solid #ccc',
    borderRadius: '8px',
    fontSize: '1rem',
  },
  inputFocus: {
    borderColor: '#007bff',
    boxShadow: '0 0 8px rgba(0, 123, 255, 0.3)',
  },
  checkbox: {
    appearance: 'none',
    width: '1.25rem',
    height: '1.25rem',
    border: '2px solid #ccc',
    borderRadius: '4px',
    marginRight: '0.5rem',
    verticalAlign: 'middle',
    cursor: 'pointer',
  },
  checkboxChecked: {
    backgroundColor: '#007bff',
    backgroundSize: 'cover',
    borderColor: '#007bff',
  },
  radio: {
    appearance: 'none',
    width: '1.25rem',
    height: '1.25rem',
    border: '2px solid #ccc',
    borderRadius: '50%',
    marginRight: '0.5rem',
    verticalAlign: 'middle',
    cursor: 'pointer',
  },
  radioChecked: {
    backgroundColor: '#007bff',
    backgroundSize: 'cover',
    borderColor: '#007bff',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  radioContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  containerLabel: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 400,
    cursor: 'pointer',
    margin: 0,
  },
  formButtons: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    marginTop: '1rem',
  },
  button: {
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0.75rem 2rem',
  },
  resetButton: {
    backgroundColor: '#6c757d',
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },
  fieldError: {
    color: '#dc3545',
    fontSize: '0.875rem',
    marginLeft: '120px',
    flex: 1,
  },
  formError: {
    color: '#dc3545',
    fontSize: '0.875rem',
    marginTop: '0.5rem',
    textAlign: 'center',
  },
  formErrorBoundary: {
    color: '#dc3545',
    padding: '1rem',
    border: '1px solid #dc3545',
    borderRadius: '8px',
    backgroundColor: '#fff5f5',
    textAlign: 'center',
  },
  formErrorBoundaryH2: {
    fontSize: '1.2rem',
    marginBottom: '0.5rem',
  },
  formErrorBoundaryP: {
    fontSize: '0.875rem',
    margin: '0.25rem 0',
  },
};

// Error Boundary Component
class FormErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error in Form component:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.formErrorBoundary}>
          <h2 style={styles.formErrorBoundaryH2}>Something went wrong in the Form.</h2>
          <p style={styles.formErrorBoundaryP}>{this.state.error?.message || "Unknown error"}</p>
          <p style={styles.formErrorBoundaryP}>Please check your initialValues structure or contact support.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Utility to flatten nested objects and arrays into a single-level object with dot-notation keys
const flattenObject = (obj, prefix = '') => {
  const result = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value)) {
      const hasObjects = value.some(item => item && typeof item === 'object' && !Array.isArray(item));
      const hasStrings = value.some(item => typeof item === 'string');

      if (hasObjects) {
        value.forEach((item, index) => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            Object.assign(result, flattenObject(item, `${newKey}[${index}]`));
          }
        });
      }

      if (hasStrings) {
        const stringOptions = value.filter(item => typeof item === 'string');
        if (stringOptions.length > 0) {
          result[newKey] = stringOptions;
        }
      }
    } else if (value && typeof value === 'object' && !('value' in value)) {
      Object.assign(result, flattenObject(value, newKey));
    } else {
      result[newKey] = value;
    }
  });
  return result;
};

// Utility to group flattened keys by their top-level key (for nested fields only)
const groupFields = (flattenedObj) => {
  const grouped = {};
  const flatFields = [];

  Object.keys(flattenedObj).forEach((key) => {
    if (key.includes('.') || key.includes('[')) {
      const [group] = key.split(/\[|\./);
      grouped[group] = grouped[group] || [];
      grouped[group].push(key);
    } else {
      flatFields.push(key);
    }
  });

  return { flatFields, groupedFields: grouped };
};

// Utility to extract the actual value and optional flag from a field
const getFieldConfig = (field) => {
  if (field && typeof field === 'object' && 'value' in field) {
    return { value: field.value, optional: !!field.optional };
  }
  return { value: field, optional: false };
};

// Utility to generate a display label from a key (e.g., "study.degree" → "Study Degree")
const generateLabel = (key) => {
  return key
    .split(/\[|\./)
    .filter(part => !part.match(/^\d+\]$/))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// Default validation function
const defaultValidate = (values, initialValuesFlat) => {
  const errors = {};
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\d{10}$/;

  Object.keys(initialValuesFlat).forEach((key) => {
    const { value, optional } = getFieldConfig(initialValuesFlat[key]);
    const fieldValue = values[key];

    // Skip optional fields if empty
    if (optional && (!fieldValue || fieldValue.trim() === '')) {
      return;
    }

    // Required string fields
    if (typeof value === 'string' && (!fieldValue || !fieldValue.trim())) {
      errors[key] = `${generateLabel(key)} is required`;
    }

    // Email validation
    if (key === 'email' && fieldValue && !emailRegex.test(fieldValue)) {
      errors[key] = 'Invalid email format';
    }

    // Phone validation
    if (key === 'phone' && fieldValue && !phoneRegex.test(fieldValue)) {
      errors[key] = 'Phone number must be exactly 10 digits';
    }

    // Array (e.g., gender) validation
    if (Array.isArray(value) && !fieldValue) {
      errors[key] = `Please select a ${generateLabel(key).toLowerCase()} option`;
    }
  });

  return errors;
};

const Form = ({ initialValues, validate, onSubmit, className, customStyles, children, reset = true }) => {
  const initialValuesFlat = flattenObject(initialValues);

  const formInitialValues = {};
  Object.keys(initialValuesFlat).forEach((key) => {
    const { value } = getFieldConfig(initialValuesFlat[key]);
    formInitialValues[key] = value;
  });

  const { values, errors, isSubmitting, handleChange, handleSubmit, resetForm } = useForm(
    formInitialValues,
    (values) => (validate ? validate(values) : defaultValidate(values, initialValuesFlat))
  );

  if (children) {
    return (
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={classNames('custom-form', className, customStyles)}
        style={styles.customForm}
      >
        {children({ values: values || {}, errors, handleChange, isSubmitting, resetForm })}
        {errors.submit && <div style={styles.formError}>{errors.submit}</div>}
      </form>
    );
  }

  const { flatFields, groupedFields } = groupFields(initialValuesFlat);

  const renderField = (key, isNested = false, group = '') => {
    const { value } = getFieldConfig(initialValuesFlat[key]);
    const fieldType = typeof value;
    const isArray = Array.isArray(value);
    const label = isNested ? generateLabel(key.replace(`${group}.`, '')) : generateLabel(key);

    if (fieldType === 'string' && !isArray) {
      return (
        <div key={key} style={{ ...styles.formField, ...(isNested ? styles.nestedField : {}) }}>
          <label htmlFor={key} style={styles.label}>{label}:</label>
          <input
            id={key}
            name={key}
            value={values[key] || ''}
            onChange={handleChange}
            placeholder={`Enter ${label.toLowerCase()}`}
            style={styles.input}
          />
          {errors[key] && <span style={styles.fieldError}>{errors[key]}</span>}
        </div>
      );
    }

    if (fieldType === 'boolean') {
      return (
        <div key={key} style={{ ...styles.formField, ...(isNested ? styles.nestedField : {}) }}>
          <label style={styles.label}>{label}:</label>
          <div style={styles.checkboxContainer}>
            <label style={styles.containerLabel}>
              <input
                type="checkbox"
                name={key}
                checked={values[key] || false}
                onChange={(e) => {
                  const syntheticEvent = {
                    target: {
                      name: e.target.name,
                      value: e.target.checked,
                    },
                  };
                  handleChange(syntheticEvent);
                }}
                style={values[key] ? { ...styles.checkbox, ...styles.checkboxChecked } : styles.checkbox}
              />
              {values[key] ? 'Yes' : 'No'}
            </label>
          </div>
        </div>
      );
    }

    if (isArray) {
      return (
        <div key={key} style={{ ...styles.formField, ...(isNested ? styles.nestedField : {}) }}>
          <label style={styles.label}>{isNested && group === key ? 'Options' : label}:</label>
          <div style={styles.radioContainer}>
            {value.map((option) => (
              <label key={String(option)} style={styles.containerLabel}>
                <input
                  type="radio"
                  name={key}
                  value={option}
                  checked={values[key] === option}
                  onChange={handleChange}
                  style={values[key] === option ? { ...styles.radio, ...styles.radioChecked } : styles.radio}
                />
                {typeof option === 'string' ? option.charAt(0).toUpperCase() + option.slice(1) : String(option)}
              </label>
            ))}
          </div>
          {errors[key] && <span style={styles.fieldError}>{errors[key]}</span>}
        </div>
      );
    }

    return null;
  };

  return (
    <FormErrorBoundary>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={classNames('custom-form', className, customStyles)}
        style={styles.customForm}
      >
        {flatFields.map((key) => renderField(key))}
        {Object.keys(groupedFields).map((group) => (
          <div key={group} style={styles.fieldGroup}>
            <div style={styles.formField}>
              <label style={styles.label}>{generateLabel(group)}:</label>
            </div>
            {groupedFields[group].map((key) => renderField(key, true, group))}
          </div>
        ))}
        <div style={styles.formButtons}>
          <button type="submit" disabled={isSubmitting} style={isSubmitting ? { ...styles.button, ...styles.buttonDisabled } : styles.button}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
          {reset && (
            <button
              type="button"
              onClick={resetForm}
              disabled={isSubmitting}
              style={isSubmitting ? { ...styles.button, ...styles.resetButton, ...styles.buttonDisabled } : { ...styles.button, ...styles.resetButton }}
            >
              Reset
            </button>
          )}
        </div>
        {errors.submit && <div style={styles.formError}>{errors.submit}</div>}
      </form>
    </FormErrorBoundary>
  );
};

Form.propTypes = {
  initialValues: PropTypes.object.isRequired,
  validate: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  customStyles: PropTypes.string,
  children: PropTypes.func,
  reset: PropTypes.bool,
};

Form.defaultProps = {
  reset: true,
};

export default Form;